import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { BasisClientService } from '@zaoblako/web/core/services/basis/basis-client.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';

@Component({
  selector: 'crm-bank-details-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SafeHtmlPipe],
  templateUrl: './bank-details-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BankDetailsSingleSelectComponent implements OnInit, OnChanges {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public bankDetails: BankDetailsFindInterface[] = [];

  @Input() public selectedBankDetail: BankDetailsFindInterface;
  @Output() public selectedBankDetailChange = new EventEmitter<BankDetailsFindInterface>();
  @Output() public reloadBankDetails = new EventEmitter<void>();

  @Input() public customRequest = false;
  @Input() public labelInput = false;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = false;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public appendTo: string;

  @Input() public onlyForCompanies = true;
  @Input() public companyIds: number[] = [];

  public constructor(
    private readonly basisClientService: BasisClientService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly globalService: GlobalService
  ) {}

  public ngOnInit(): void {
    this.loadBankDetailsFromApi();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyIds'] && !changes['companyIds'].firstChange) {
      this.loadBankDetailsFromApi();
    }
    if (changes['customRequest'] && !changes['customRequest'].firstChange && !this.customRequest) {
      this.loadBankDetailsFromApi();
    }
  }

  private loadBankDetailsFromApi = (): void => {
    if (this.customRequest) {
      return;
    }
    if (!this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled) {
      return;
    }
    if (!this.companyIds?.length) {
      return;
    }

    this.basisClientService
      .bankDetailsSearch({ onlyForCompanies: this.onlyForCompanies, companyIds: this.companyIds })
      .subscribe({
        next: (bankDetails) => {
          this.bankDetails = this.withSelectedBankDetail(bankDetails);
          this.cdRef.markForCheck();
        }
      });
  };

  private withSelectedBankDetail = (bankDetails: BankDetailsFindInterface[]): BankDetailsFindInterface[] => {
    if (!this.selectedBankDetail?.id) {
      return bankDetails;
    }
    if (bankDetails.some(({ id }) => id === this.selectedBankDetail.id)) {
      return bankDetails;
    }
    return [this.selectedBankDetail, ...bankDetails];
  };

  public toBankDetail(bankDetail: any): BankDetailsFindInterface {
    return bankDetail as BankDetailsFindInterface;
  }

  public highlightMatchText(text: string): string {
    return HighlightMatchTextHelper.highlightMatchText(text, this.singleSelectComponent?.dropdown?.filterValue);
  }

  public onShowPanel(): void {
    if (this.customRequest && this.companyIds?.length && !this.bankDetails?.length) {
      this.reloadBankDetails.emit();
    }
  }
}
